import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { 
  Target, 
  Eye, 
  Heart, 
  Users, 
  Award, 
  BookOpen,
  CheckCircle,
  ArrowRight
} from 'lucide-react';
import { Link } from 'react-router-dom';

const AboutSection = ({ company }) => {
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true
  });

  // Use company data with fallbacks
  const mission = company?.mission || "Our mission is to provide world-class engineering training that empowers professionals to excel in their careers and drive innovation in their industries.";
  const vision = company?.vision || "To be the global leader in engineering education, recognized for excellence, innovation, and the success of our graduates.";

  let values = company?.values ||  [
    'Excellence in Education',
    'Innovation in Teaching',
    'Industry Relevance',
    'Student Success',
    'Continuous Improvement',
    'Ethical Practice'
  ];

  values =  JSON.parse(company?.values || JSON.stringify(values));
  const features = [
    {
      icon: BookOpen,
      title: 'Comprehensive Curriculum',
      description: 'Industry-relevant courses designed by experts with real-world applications.'
    },
    {
      icon: Users,
      title: 'Expert Instructors',
      description: 'Learn from industry professionals with years of hands-on experience.'
    },
    {
      icon: Award,
      title: 'Certification Programs',
      description: 'Earn recognized certifications that boost your career prospects.'
    },
    {
      icon: CheckCircle,
      title: 'Practical Learning',
      description: 'Hands-on projects and real-world case studies for better understanding.'
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: 'easeOut'
      }
    }
  };

  return (
    <section ref={ref} className="section-padding bg-white dark:bg-gray-900">
      <div className="container-custom">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center"
        >
          {/* Left Content */}
          <motion.div variants={itemVariants}>
            <div className="space-y-8">
              <div>
                <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-primary-100 text-primary-800 dark:bg-primary-900 dark:text-primary-200 mb-4">
                  About Prime Edge
                </span>
                <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-6">
                </h2>
                <p className="text-xl text-gray-600 dark:text-gray-300 leading-relaxed">
                  {company?.description || 'Prime Edge is a leading engineering training company dedicated to providing world-class education and professional development opportunities for engineers worldwide.'}
                </p>
              </div>

              {/* Mission & Vision */}
              <div className="space-y-6">
                <div className="p-6 bg-primary-50 dark:bg-primary-900/20 rounded-lg">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-primary-600 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Target className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Our Mission</h3>
                      <p className="text-gray-600 dark:text-gray-300">
                        {mission}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="p-6 bg-accent-50 dark:bg-accent-900/20 rounded-lg">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-accent-600 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Eye className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Our Vision</h3>
                      <p className="text-gray-600 dark:text-gray-300">
                        {vision}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Values */}
              <div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Our Values</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {values.map((value, index) => (
                    <motion.div
                      key={index}
                      variants={itemVariants}
                      className="flex items-center space-x-3"
                    >
                      <Heart className="w-5 h-5 text-primary-600 flex-shrink-0" />
                      <span className="text-gray-700 dark:text-gray-300">{value}</span>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right Content - About Image and Features Grid */}
          <motion.div variants={itemVariants}>
            {/* About Image */}
            {company?.aboutImage && (
              <motion.div 
                variants={itemVariants}
                className="mb-8 rounded-xl overflow-hidden shadow-xl"
              >
                <img 
                  src={company.aboutImage} 
                  alt="About Prime Edge" 
                  className="w-full h-auto object-cover rounded-xl"
                />
              </motion.div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {features.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <motion.div
                    key={index}
                    variants={itemVariants}
                    whileHover={{ y: -5 }}
                    className="p-6 bg-white dark:bg-gray-800 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 dark:border-gray-700"
                  >
                    <div className="w-12 h-12 bg-primary-100 dark:bg-primary-900 rounded-lg flex items-center justify-center mb-4">
                      <Icon className="w-6 h-6 text-primary-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                      {feature.title}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-300 text-sm">
                      {feature.description}
                    </p>
                  </motion.div>
                );
              })}
            </div>

            {/* CTA */}
            <motion.div
              variants={itemVariants}
              className="mt-8 p-6 bg-gradient-to-r from-primary-600 to-accent-600 rounded-xl text-white"
            >
              <h3 className="text-xl font-semibold mb-2">Ready to Start Your Journey?</h3>
              <p className="text-primary-100 mb-4">
                Join thousands of engineers who have transformed their careers with Prime Edge.
              </p>
              <Link to={"/courses"} className="inline-flex items-center px-6 py-3 bg-white text-primary-600 rounded-lg font-medium hover:bg-gray-100 transition-colors">
                Explore Courses
                <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default AboutSection;

